// Google Routes API service layer
import type {
  RouteMatrixRequest,
  RouteMatrixElement,
  ComputeRoutesRequest,
  ComputeRoutesResponse,
  Coordinates,
} from "../types/routes";
import {
  coordinatesToGoogleWaypoint,
  validateCoordinates,
  formatCoordinatesForLogging,
} from "../utils/routeUtils";

// Error handling class
export class GoogleRoutesError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public apiResponse?: string,
    public originalError?: Error
  ) {
    super(message);
    this.name = "GoogleRoutesError";
  }
}

// Google Routes Matrix API integration
export const makeRouteMatrixRequest = async (
  origins: Coordinates[],
  destinations: Coordinates[]
): Promise<RouteMatrixElement[]> => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    throw new GoogleRoutesError("Google Maps API key not configured");
  }

  // Validate inputs
  if (!origins.length || !destinations.length) {
    throw new GoogleRoutesError("Origins and destinations are required");
  }

  // Validate coordinates
  for (const origin of origins) {
    if (!validateCoordinates(origin)) {
      throw new GoogleRoutesError(
        `Invalid origin coordinates: ${formatCoordinatesForLogging(origin)}`
      );
    }
  }

  for (const destination of destinations) {
    if (!validateCoordinates(destination)) {
      throw new GoogleRoutesError(
        `Invalid destination coordinates: ${formatCoordinatesForLogging(
          destination
        )}`
      );
    }
  }

  const request: RouteMatrixRequest = {
    origins: origins.map(coordinatesToGoogleWaypoint),
    destinations: destinations.map(coordinatesToGoogleWaypoint),
    travelMode: "DRIVE",
    routingPreference: "TRAFFIC_AWARE",
    languageCode: "sr",
    regionCode: "RS",
    units: "METRIC",
  };

  console.log("[GoogleRoutes] Making Matrix API request:", {
    origins: origins.length,
    destinations: destinations.length,
    originCoords: origins.map(formatCoordinatesForLogging),
    destinationCoords: destinations.map(formatCoordinatesForLogging),
  });

  try {
    const response = await fetch(
      "https://routes.googleapis.com/distanceMatrix/v2:computeRouteMatrix",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": apiKey,
          "X-Goog-FieldMask":
            "originIndex,destinationIndex,status,distanceMeters,duration",
        },
        body: JSON.stringify(request),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("[GoogleRoutes] API error:", response.status, errorText);
      throw new GoogleRoutesError(
        `Google Routes API error: ${response.status}`,
        response.status,
        errorText
      );
    }

    const data = await response.json();

    // Handle different response formats from Google
    let elements: RouteMatrixElement[] = [];

    if (Array.isArray(data)) {
      elements = data;
    } else if (data.rows && Array.isArray(data.rows)) {
      // Handle Google's standard response format: { rows: [{ elements: [...] }] }
      elements = data.rows.flatMap((row: any) => row.elements || []);
    } else if (data.elements && Array.isArray(data.elements)) {
      elements = data.elements;
    } else {
      console.warn("[GoogleRoutes] Unexpected response format:", data);
      elements = [data]; // Fallback for single element response
    }

    console.log("[GoogleRoutes] Matrix API response:", {
      elementCount: elements.length,
      successful: elements.filter((e) => !e.status?.code || e.status.code === 0)
        .length,
      failed: elements.filter((e) => e.status?.code && e.status.code !== 0)
        .length,
    });

    return elements;
  } catch (error: any) {
    console.error("[GoogleRoutes] Request failed:", error);

    if (error instanceof GoogleRoutesError) {
      throw error;
    }

    throw new GoogleRoutesError(
      error.message || "Network request failed",
      undefined,
      undefined,
      error
    );
  }
};

// Optional: Compute Routes API for polylines (future enhancement)
export const makeComputeRoutesRequest = async (
  origin: Coordinates,
  destination: Coordinates
): Promise<string | null> => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    console.warn("Google Maps API key not configured for polylines");
    return null;
  }

  // Validate coordinates
  if (!validateCoordinates(origin) || !validateCoordinates(destination)) {
    console.error("Invalid coordinates for polyline request");
    return null;
  }

  const request: ComputeRoutesRequest = {
    origin: {
      location: {
        latLng: { latitude: origin[0], longitude: origin[1] },
      },
    },
    destination: {
      location: {
        latLng: { latitude: destination[0], longitude: destination[1] },
      },
    },
    travelMode: "DRIVE",
    routingPreference: "TRAFFIC_AWARE",
    languageCode: "sr",
    regionCode: "RS",
    polylineQuality: "OVERVIEW", // Use OVERVIEW for better performance
    polylineEncoding: "ENCODED_POLYLINE",
  };

  try {
    console.log("[GoogleRoutes] Requesting polyline for route:", {
      from: formatCoordinatesForLogging(origin),
      to: formatCoordinatesForLogging(destination),
    });

    const response = await fetch(
      "https://routes.googleapis.com/directions/v2:computeRoutes",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": apiKey,
          "X-Goog-FieldMask": "routes.polyline.encodedPolyline",
        },
        body: JSON.stringify(request),
      }
    );

    if (!response.ok) {
      console.error("[GoogleRoutes] Polyline API error:", response.status);
      return null;
    }

    const data: ComputeRoutesResponse = await response.json();
    const polyline = data.routes?.[0]?.polyline?.encodedPolyline;

    if (polyline) {
      console.log("[GoogleRoutes] Polyline received, length:", polyline.length);
    } else {
      console.warn("[GoogleRoutes] No polyline in response");
    }

    return polyline || null;
  } catch (error) {
    console.error("[GoogleRoutes] Polyline request failed:", error);
    return null;
  }
};

// Error handling utilities
export const handleRoutesAPIError = (error: any): GoogleRoutesError => {
  if (error instanceof GoogleRoutesError) {
    return error;
  }

  return new GoogleRoutesError(
    error.message || "Unknown Google Routes API error",
    error.statusCode,
    error.apiResponse,
    error
  );
};

// Rate limiting helper (optional - for future use)
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 50; // 50ms = max 20 RPS (well under Google's 100 QPS limit)

export const respectRateLimit = async (): Promise<void> => {
  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;

  if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
    const waitTime = MIN_REQUEST_INTERVAL - timeSinceLastRequest;
    console.log("[GoogleRoutes] Rate limiting: waiting", waitTime, "ms");
    await new Promise((resolve) => setTimeout(resolve, waitTime));
  }

  lastRequestTime = Date.now();
};
